import React from "react";
import { styled } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import { Container, Typography, Grid, Box } from "@mui/material";
import { Carousel } from "react-responsive-carousel";
import "./../../../node_modules/react-responsive-carousel/lib/styles/carousel.css"; // requires a loader
import TestimonialItems from "../Home/Testimonials.json";

/**
 * PM5 T2 (money path, batch "freetrial-buynow") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * The member-testimonial carousel on /free-trial and the doors-open /buy
 * landing. Styling-only conversion; every `<Carousel>` prop is unchanged.
 *
 * The `& .carousel .slide` rule targets `react-responsive-carousel`'s own
 * classes, not MUI slots, so the `FfV5` prefix does not touch it (guide §1.2b
 * applies to `.Mui*` selectors only) — it is carried over verbatim.
 */

// was: useStyles().background — a bare <div>, which has no `sx` prop.
const Background = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.ffNavyLighter,
  padding: theme.spacing(6, 0),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(8, 0),
  },
}));

// was: useStyles().title
const titleSx = (theme: Theme) => ({
  letterSpacing: "-2px",
  fontSize: "2rem",
  lineHeight: "2.2rem",
  fontWeight: 600,
  textShadow: "-2px 2px 2px #171717",
  "& span": {
    color: "background.ffGreen",
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "3rem",
    lineHeight: "3.2rem",
  },
});

// was: useStyles().titleLarge
const titleLargeSx = (theme: Theme) => ({
  letterSpacing: "-2px",
  fontSize: "2.8rem",
  lineHeight: "2.8rem",
  fontWeight: 600,
  textShadow: "-2px 2px 2px #171717",
  [theme.breakpoints.up("sm")]: {
    fontSize: "5.4rem",
    lineHeight: "5rem",
  },
});

// was: useStyles().carouselContainer
const carouselContainerSx = {
  marginBottom: "16px",
  "& .carousel .slide": {
    backgroundColor: "background.ffNavyLighter",
    marginBottom: "24px",
    padding: "24px 8px",
    position: "relative",
  },
};

// was: useStyles().testimonialContent
const testimonialContentSx = (theme: Theme) => ({
  padding: "24px",
  backgroundColor: "background.ffNavyLighter",
  fontSize: "1.1rem",
  lineHeight: "1.3rem",
  fontWeight: 500,
  letterSpacing: "-1px",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.3rem",
    lineHeight: "1.5rem",
  },
});

const Bottom = () => {
  const testimonialObject = TestimonialItems;

  return (
    <>
      <Background>
        <Container maxWidth="md" fixed>
          {/* v5+ Grid: `item` is gone and the breakpoint props collapse into
              `size`. No `spacing` on this container, so widths are unchanged. */}
          <Grid container>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h2" sx={titleSx}>
                What The
              </Typography>
              <Typography variant="h2" sx={titleLargeSx}>
                Members Say
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }} sx={carouselContainerSx}>
              <Carousel
                useKeyboardArrows={true}
                showArrows={false}
                interval={7000}
                showThumbs={false}
                infiniteLoop={true}
                showStatus={false}
                autoPlay={true}
                showIndicators={true}
                transitionTime={1200}
              >
                {testimonialObject.map((i, idx) => (
                  <Box key={idx}>
                    <Typography variant="body1" sx={testimonialContentSx}>
                      {i.testimonial}
                    </Typography>
                  </Box>
                ))}
              </Carousel>
            </Grid>
          </Grid>
        </Container>
      </Background>
    </>
  );
};

export default Bottom;
